import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Star, Trophy, Zap, Target, TrendingUp, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { LAYERS } from "@/lib/layers";
import { t } from "@/lib/translations";
import badgeGold from "@/assets/badges/badge_star_gold.png";
import badgeComplete from "@/assets/badges/badge_complete.png";

// Stat Card Component
function StatCard({ icon: Icon, label, value, color, delay = 0, badge }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", damping: 14 }}
    >
      <Card className="rounded-3xl border-0 shadow-lg overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0"
              style={{ background: color + "22", border: `2px solid ${color}33` }}
            >
              {badge ? (
                <img src={badge} alt="badge" className="w-10 h-10 object-contain" />
              ) : (
                <Icon className="w-7 h-7" style={{ color }} />
              )}
            </div>
            <div>
              <div className="text-sm font-bold text-slate-400 leading-tight">{label}</div>
              <div className="font-display text-3xl font-bold text-slate-800">{value}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Custom tooltip for bar chart
function CustomBarTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-3 border border-slate-100">
        <div className="font-display font-bold text-slate-700">{label}</div>
        {payload.map((p) => (
          <div key={p.name} className="text-sm font-bold" style={{ color: p.color }}>
            {p.name}: {p.value}
          </div>
        ))}
      </div>
    );
  }
  return null;
}

export default function ProgressReport({ child, language }) {
  const [summary, setSummary] = useState(null);
  const [researchSummary, setResearchSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!child) return;
    setLoading(true);
    Promise.all([
      api.getProgressSummary(child.id).catch(() => null),
      api.getResearchSummary(child.id).catch(() => null),
    ]).then(([prog, research]) => {
      setSummary(prog);
      setResearchSummary(research);
      setLoading(false);
    });
  }, [child]);

  if (!child) {
    return (
      <Card className="rounded-3xl bg-white/70 border-dashed border-2">
        <CardContent className="p-12 text-center text-slate-400 font-bold text-lg">
          {language === "id" ? "Pilih anak untuk melihat laporan" : "Select a child to view report"}
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full border-4 border-sky-200 border-t-sky-500"
        />
        <div className="font-bold text-slate-400">
          {language === "id" ? "Memuat laporan..." : "Loading report..."}
        </div>
      </div>
    );
  }

  const totalPlays = summary?.total_plays || 0;
  const totalStars = child.stars || 0;
  const accuracy = researchSummary?.accuracy
    ? `${Math.round(researchSummary.accuracy * 100)}%`
    : "—";
  const sessions = researchSummary?.sessions || 0;

  // Data for Layer Bar Chart
  const layerData = Object.entries(summary?.summary || {}).map(([layer, data]) => ({
    name: `Layer ${layer}`,
    label: LAYERS[layer] ? t(LAYERS[layer].themeKey, language) : `Layer ${layer}`,
    "Sesi Main": data.plays,
    "⭐ Bintang": data.stars,
    color: LAYERS[layer]?.color || "#38BDF8",
  }));

  // Data for accuracy Pie Chart
  const correct = researchSummary?.by_event_type?.attempt_correct || 0;
  const wrong = researchSummary?.by_event_type?.attempt_wrong || 0;
  const totalAttempts = correct + wrong;
  const accuracyPieData = totalAttempts > 0
    ? [
        { name: language === "id" ? "Benar" : "Correct", value: correct, color: "#4ADE80" },
        { name: language === "id" ? "Salah" : "Wrong", value: wrong, color: "#F87171" },
      ]
    : null;

  // Data for game type breakdown
  const gameTypeData = Object.entries(researchSummary?.by_game || {})
    .slice(0, 6)
    .map(([game, events]) => {
      const c = events.attempt_correct || 0;
      const w = events.attempt_wrong || 0;
      return {
        name: game.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        Benar: c,
        Salah: w,
      };
    });

  return (
    <div className="space-y-6">
      {/* Child Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-800">
            📊 Laporan: <span className="text-sky-500">{child.name}</span>
          </h2>
          <p className="text-slate-500 font-bold text-sm mt-0.5">
            {child.age} {child.age === 1 ? t("year", language) : t("years", language)} •{" "}
            {LAYERS[child.age] ? t(LAYERS[child.age].themeKey, language) : ""}
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-2xl font-bold flex items-center gap-2 border-2"
          onClick={() => window.open(api.researchCsvUrl(child.id), "_blank")}
        >
          <Download className="w-4 h-4" />
          {language === "id" ? "Ekspor CSV" : "Export CSV"}
        </Button>
      </motion.div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Star}
          label={language === "id" ? "Total Bintang" : "Total Stars"}
          value={totalStars}
          color="#F59E0B"
          delay={0}
          badge={totalStars >= 10 ? badgeGold : null}
        />
        <StatCard
          icon={Zap}
          label={language === "id" ? "Total Sesi" : "Total Plays"}
          value={totalPlays}
          color="#38BDF8"
          delay={0.05}
        />
        <StatCard
          icon={Target}
          label={language === "id" ? "Akurasi" : "Accuracy"}
          value={accuracy}
          color="#4ADE80"
          delay={0.1}
          badge={accuracy !== "—" && parseInt(accuracy) >= 80 ? badgeComplete : null}
        />
        <StatCard
          icon={TrendingUp}
          label={language === "id" ? "Sesi Riset" : "Research Sessions"}
          value={sessions}
          color="#A78BFA"
          delay={0.15}
        />
      </div>

      {/* Layer Activity Chart */}
      {layerData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="rounded-3xl border-0 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-xl text-slate-700">
                🎮 {language === "id" ? "Aktivitas Per Layer" : "Activity Per Layer"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={layerData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontFamily: "Fredoka", fontWeight: 700, fontSize: 12 }}
                    stroke="#94a3b8"
                  />
                  <YAxis tick={{ fontFamily: "Nunito", fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="Sesi Main" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="⭐ Bintang" fill="#FCD34D" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Bottom Row: Accuracy Pie + Game Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Accuracy Pie */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="rounded-3xl border-0 shadow-lg h-full">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-xl text-slate-700">
                🎯 {language === "id" ? "Akurasi Jawaban" : "Answer Accuracy"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {accuracyPieData ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={accuracyPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {accuracyPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Legend
                      formatter={(value) => (
                        <span style={{ fontFamily: "Nunito", fontWeight: 700, fontSize: 13 }}>
                          {value}
                        </span>
                      )}
                    />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 font-bold text-center">
                  {language === "id"
                    ? "Belum ada data jawaban.\nMulai bermain dulu!"
                    : "No answer data yet.\nStart playing first!"}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Per-Game Breakdown */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="rounded-3xl border-0 shadow-lg h-full">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-xl text-slate-700">
                🕹️ {language === "id" ? "Per Game" : "Per Game"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {gameTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    layout="vertical"
                    data={gameTypeData}
                    margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={90}
                      tick={{ fontFamily: "Nunito", fontWeight: 700, fontSize: 10 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Bar dataKey="Benar" fill="#4ADE80" radius={[0, 6, 6, 0]} stackId="a" />
                    <Bar dataKey="Salah" fill="#F87171" radius={[0, 6, 6, 0]} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 font-bold text-center">
                  {language === "id"
                    ? "Belum ada data game.\nAjak si kecil bermain!"
                    : "No game data yet.\nStart playing with your child!"}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Footer insight */}
      {totalPlays > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="rounded-3xl bg-gradient-to-r from-sky-50 to-purple-50 border-0 shadow-md">
            <CardContent className="p-5 flex items-center gap-4">
              <Trophy className="w-10 h-10 text-amber-500 flex-shrink-0" />
              <div>
                <div className="font-display font-bold text-lg text-slate-700">
                  {language === "id" ? `${child.name} sudah main ${totalPlays} kali! 🎉` : `${child.name} played ${totalPlays} times! 🎉`}
                </div>
                <div className="text-slate-500 text-sm font-bold">
                  {accuracy !== "—"
                    ? (language === "id"
                        ? `Tingkat keberhasilan: ${accuracy} — ${parseInt(accuracy) >= 70 ? "Luar biasa! 🌟" : "Terus semangat! 💪"}`
                        : `Success rate: ${accuracy} — ${parseInt(accuracy) >= 70 ? "Amazing! 🌟" : "Keep going! 💪"}`)
                    : (language === "id" ? "Aktifkan Mode Penelitian untuk data lebih detail." : "Enable Research Mode for detailed data.")}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
